import React, { useMemo } from 'react';
import {
  Animated,
  Modal,
  StyleSheet,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { mvs } from 'react-native-size-matters';

import { ContextualMenuShellProps } from './types';
import { COLORS, isTablet } from '../../constants';
import useModalPresentationAnimation from '../../hooks/useModalPresentationAnimation';
import { FloatingExitButton } from '../FloatingExitButton';

export const ContextualMenuShell: React.FC<ContextualMenuShellProps> = ({
  visible,
  children,
  onRequestClose,
}) => {
  const insets = useSafeAreaInsets();
  const { mounted, backdropStyle, menuAnimatedStyle } = useModalPresentationAnimation(
    visible,
    isTablet,
  );

  const paddingStyle = useMemo(
    () => ({ paddingBottom: mvs(10) + insets.bottom }),
    [insets.bottom],
  );

  // menu animation comes from hook

  return (
    <Modal
      // use custom animations so the backdrop can fade independently from the menu slide
      animationType={'none'}
      transparent={true}
      visible={mounted}
      statusBarTranslucent={true}
      onRequestClose={onRequestClose}
    >
      <TouchableWithoutFeedback onPress={onRequestClose}>
        <View style={styles.overlay}>
          {/* backdrop that fades in/out */}
          <Animated.View
            pointerEvents="none"
            style={[StyleSheet.absoluteFillObject, styles.backdrop, backdropStyle]}
          />

          <TouchableWithoutFeedback>
            <Animated.View
              style={[styles.menuContainer, paddingStyle, menuAnimatedStyle]}
            >
              <View style={styles.header}>
                <FloatingExitButton
                  onPress={onRequestClose}
                  containerStyle={styles.closeButton}
                />
              </View>
              {children}
            </Animated.View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: isTablet ? 'center' : 'flex-end',
    alignItems: isTablet ? 'center' : 'stretch',
  },
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
  },
  menuContainer: {
    backgroundColor: COLORS.WHITE,
    width: isTablet ? '80%' : '100%',
    maxWidth: isTablet ? 500 : undefined,
    borderTopLeftRadius: isTablet ? mvs(20) : 10,
    borderTopRightRadius: isTablet ? mvs(20) : 10,
    borderBottomLeftRadius: isTablet ? mvs(20) : 0,
    borderBottomRightRadius: isTablet ? mvs(20) : 0,
    padding: mvs(24),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    position: 'relative',
  },
  closeButton: {
    position: 'absolute',
    top: isTablet ? mvs(-38) : mvs(-44),
    right: isTablet ? mvs(-12) : 0,
  },
});
